import React, { useRef, useState } from 'react'
import { useHistory } from 'react-router';
import { connect } from 'react-redux';
import { MdAutoAwesome, MdAddCircleOutline, MdLock, MdSearch, MdBarChart } from 'react-icons/md';
import PageHeader from 'components/pageHeader';
import { setFromClassroom } from 'actions/classRoom';
import { setQuickAccessItem } from 'actions/userManagementV2';
import { clearGenerationSnapshot } from 'pages/Test/components/generationCache';
import { categories, modules, initiallyDisabled, credits, mostUsed, frequentlyUsed, newlyAdded, findModule } from './aiModules';
import './styles.css';

// Soft pastel palettes for the drawn thumbnails, in the style of the Tests card art
const tones = {
    purple: { from: '#EFEAFF', to: '#DCD2FB', blob: '#C9BBF5', ink: '#594099' },
    mint: { from: '#E3F8F0', to: '#C6EFDF', blob: '#9FE0C6', ink: '#1E9E73' },
    peach: { from: '#FFF0E8', to: '#FFDCCB', blob: '#FFC3A8', ink: '#E5703A' },
    yellow: { from: '#FFF8DF', to: '#FFEDB0', blob: '#FFDF7E', ink: '#D99A00' },
    blue: { from: '#E7F1FF', to: '#CFE2FF', blob: '#A9CBFF', ink: '#1F6FE0' },
    pink: { from: '#FFEAF1', to: '#FFD3E2', blob: '#FFB3CC', ink: '#D9467A' },
}

const formatNumber = (n) => n.toLocaleString('en-US')

const ACTIVE_TAB = 'active'

// A drawn thumbnail: the tool's icon on a tilted card over a pastel background
const DrawnThumb = ({ icon: Icon, tone }) => {
    const c = tones[tone] || tones.purple
    return (
        <div className='ai-tool-thumb' style={{ background: `linear-gradient(135deg, ${c.from}, ${c.to})` }}>
            <div className='blob' style={{ width: 160, height: 160, left: -50, bottom: -70, backgroundColor: c.blob, opacity: 0.6 }} />
            <div className='blob' style={{ width: 90, height: 90, right: -20, top: -30, backgroundColor: c.blob, opacity: 0.45 }} />
            <MdAutoAwesome className='sparkle' style={{ left: '22%', top: 26, fontSize: 26, color: c.ink, opacity: 0.8 }} />
            <MdAutoAwesome className='sparkle' style={{ right: '20%', bottom: 28, fontSize: 18, color: c.ink, opacity: 0.6 }} />
            <div className='paper'>
                <Icon style={{ fontSize: 56, color: c.ink }} />
            </div>
        </div>
    )
}

const SectionTitle = ({ children, aside }) => (
    <div className='ai-section-head'>
        <h2 className='ai-section-title'>{children}</h2>
        {aside}
    </div>
)

const CreditsCard = () => {
    const usedPercent = (credits.used / credits.total) * 100
    return (
        <div className='ai-card ai-credits-card'>
            <div className='ai-credits-circle ai-credits-circle--lg' />
            <div className='ai-credits-circle ai-credits-circle--sm' />
            <div className='ai-credits-top'>
                <div>
                    <div className='ai-eyebrow ai-eyebrow--light'>AI Credits</div>
                    <div className='ai-credits-value'>
                        <span className='ai-credits-used'>{formatNumber(credits.used)}</span>
                        <span className='ai-credits-total'>/ {formatNumber(credits.total)}</span>
                    </div>
                    <div className='ai-credits-note'>
                        Resets {credits.resetsOn} · {formatNumber(credits.total - credits.used)} remaining
                    </div>
                </div>
                {/* TODO: open the credits purchase flow once it exists */}
                <button type='button' className='ai-credits-buy'>
                    <MdAddCircleOutline />
                    Buy credits
                </button>
            </div>
            <div className='ai-credits-bar'>
                <div style={{ width: `${usedPercent}%` }} />
            </div>
            <div className='ai-credits-split'>
                {credits.split.map(({ label, percent }) => (
                    <div key={label}>
                        <span className='ai-credits-split-label'>{label}</span> <span className='ai-num'>{percent}%</span>
                    </div>
                ))}
            </div>
        </div>
    )
}

const MostUsedCard = () => {
    const module = findModule(mostUsed.moduleId)
    const Icon = module.icon
    return (
        <div className='ai-card'>
            <div className='ai-card-row'>
                <div className='ai-eyebrow'>Most used module</div>
                <div className='ai-trend-badge ai-num'>↑ {mostUsed.change}%</div>
            </div>
            <div className='ai-most-used'>
                <div className='ai-icon-tile'><Icon /></div>
                <div>
                    <div className='ai-most-used-title'>{module.title}</div>
                    <div className='ai-muted ai-small'>{formatNumber(mostUsed.generations)} generations</div>
                </div>
            </div>
            <div className='ai-spark-bars'>
                {mostUsed.trend.map((height, i) => {
                    const last = i === mostUsed.trend.length - 1
                    const recent = i >= mostUsed.trend.length - 4
                    return <div key={i} style={{ height: `${height}%`, background: last ? '#594099' : recent ? '#C9B6FF' : '#E6E0F5' }} />
                })}
            </div>
        </div>
    )
}

const FrequentlyUsedCard = () => (
    <div className='ai-card'>
        <div className='ai-eyebrow'>Frequently used</div>
        <div className='ai-frequent-list'>
            {frequentlyUsed.map(({ moduleId, label, count, bg, fg }) => {
                const Icon = findModule(moduleId).icon
                return (
                    <div key={moduleId} className='ai-frequent-row'>
                        <div className='ai-frequent-icon' style={{ background: bg, color: fg }}><Icon /></div>
                        <div className='ai-frequent-label'>{label}</div>
                        <div className='ai-num ai-frequent-count'>{count}</div>
                    </div>
                )
            })}
        </div>
    </div>
)

const NewModuleCard = ({ moduleId, desc, date, onTry }) => {
    const { title, icon: Icon } = findModule(moduleId)
    return (
        <div className='ai-card ai-new-card'>
            <div className='ai-new-badge'><span />NEW</div>
            <div className='ai-icon-tile'><Icon /></div>
            <div className='ai-new-text'>
                <div className='ai-new-title'>{title}</div>
                <div className='ai-muted ai-small ai-new-desc'>{desc}</div>
                <div className='ai-new-meta'>
                    <span className='ai-num'>Added {date}</span>
                    <span>·</span>
                    <button type='button' className='ai-link-btn' onClick={onTry}>Try it →</button>
                </div>
            </div>
        </div>
    )
}

const ModuleSwitch = ({ checked, disabled, onChange, label }) => (
    <button
        type='button'
        role='switch'
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        className={`ai-switch ${checked ? 'on' : ''}`}
        onClick={(e) => {
            // The card behind it opens the tool; the switch only turns it on or off
            e.stopPropagation()
            onChange(!checked)
        }}
    >
        <span className='ai-switch-track'><span className='ai-switch-knob' /></span>
    </button>
)

const ModuleCard = ({ module, enabled, onToggle, onOpen }) => {
    const { title, desc, image, icon, tone, usage, credits: cost, comingSoon } = module
    const clickable = !!onOpen && enabled
    return (
        <div className={`ai-tool-card ${comingSoon ? 'soon' : ''} ${clickable ? 'live' : ''}`} onClick={clickable ? onOpen : undefined}>
            <div className='ai-tool-thumb-wrap'>
                {image
                    ? <div className='ai-tool-thumb'><img src={image} alt='' /></div>
                    : <DrawnThumb icon={icon} tone={tone} />
                }
                {comingSoon && <div className='ai-tool-badge'><MdLock />Coming soon</div>}
                <div className='ai-tool-switch'>
                    <ModuleSwitch checked={enabled} disabled={comingSoon} onChange={onToggle} label={`Turn ${title} ${enabled ? 'off' : 'on'}`} />
                </div>
            </div>
            <div className='ai-tool-body'>
                <div className='ai-tool-title'>{title}</div>
                <div className='ai-tool-desc'>{desc}</div>
                {!comingSoon && (
                    <div className='ai-tool-stats ai-num'>
                        <span className='ai-tool-usage'><MdBarChart />{usage || '—'} uses</span>
                        <span>·</span>
                        <span>{cost} cr/use</span>
                    </div>
                )}
            </div>
        </div>
    )
}

const AiTools = ({ setFromClassroom, setQuickAccessItem }) => {

    //HOISTINGS
    const history = useHistory();
    const modulesRef = useRef(null)

    //USESTATES
    const [activeTab, setActiveTab] = useState(ACTIVE_TAB)
    const [search, setSearch] = useState('')
    // TODO: load and save the institute's module switches once the API exists
    const [disabled, setDisabled] = useState(initiallyDisabled)

    // HANDLERS
    const openFlows = {
        generateTest: () => {
            setQuickAccessItem({})
            // A fresh start, not a return from /create-test.
            clearGenerationSnapshot()
            setFromClassroom(false)
            history.push('/ai-modules/generate-test')
        },
        scanPdf: () => {
            setQuickAccessItem({})
            setFromClassroom(false)
            history.push('/ai-modules/scan-pdf')
        },
    }

    const isEnabled = (module) => !module.comingSoon && !disabled.includes(module.id)

    const toggleModule = (id, on) => setDisabled(list => on ? list.filter(x => x !== id) : [...list, id])

    const showCategory = (key) => {
        setSearch('')
        setActiveTab(key)
        modulesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    const allModules = Object.values(modules).flat()
    const activeModules = allModules.filter(isEnabled)

    // A search looks through every category, not just the open tab
    const term = search.trim().toLowerCase()
    const visibleModules = term
        ? allModules.filter(m => m.title.toLowerCase().includes(term) || m.desc.toLowerCase().includes(term))
        : activeTab === ACTIVE_TAB ? activeModules : modules[activeTab]
    const heading = term
        ? `Results for “${search.trim()}”`
        : activeTab === ACTIVE_TAB ? 'Active modules' : categories.find(c => c.key === activeTab).title
    const emptyMessage = term ? `No modules match “${search.trim()}”` : 'No modules are turned on yet'

    return (
        <div className='ai-page'>
            <div style={{ marginBottom: 70 }}>
                <PageHeader title="AI" />
            </div>

            <section className='ai-section'>
                <SectionTitle aside={<div className='ai-eyebrow ai-num'>LAST 30 DAYS</div>}>Overview</SectionTitle>
                <div className='ai-overview-grid'>
                    <CreditsCard />
                    <MostUsedCard />
                    <FrequentlyUsedCard />
                </div>
            </section>

            <section className='ai-section'>
                <SectionTitle>
                    Newly added <span className='ai-count-pill'>{newlyAdded.length} new</span>
                </SectionTitle>
                <div className='ai-new-grid'>
                    {newlyAdded.map(item => <NewModuleCard key={item.moduleId} {...item} onTry={() => showCategory(item.category)} />)}
                </div>
            </section>

            <div className='ai-tabs-bar' ref={modulesRef}>
                <div className='ai-tabs' role='tablist'>
                    {/* Every switched-on module, across all categories */}
                    <button type='button' role='tab' aria-selected={!term && activeTab === ACTIVE_TAB} className={`ai-tab ${!term && activeTab === ACTIVE_TAB ? 'active' : ''}`} onClick={() => showCategory(ACTIVE_TAB)}>
                        Active
                        <span className='ai-tab-count ai-num'>{activeModules.length}</span>
                    </button>
                    {categories.map(({ key, title }) => {
                        const active = !term && key === activeTab
                        return (
                            <button key={key} type='button' role='tab' aria-selected={active} className={`ai-tab ${active ? 'active' : ''}`} onClick={() => showCategory(key)}>
                                {title}
                                <span className='ai-tab-count ai-num'>{modules[key].length}</span>
                            </button>
                        )
                    })}
                </div>
                <label className='ai-search'>
                    <MdSearch />
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder='Search modules' />
                </label>
            </div>

            <section>
                <SectionTitle aside={<div className='ai-muted ai-small'>Toggle modules on or off for your institute</div>}>{heading}</SectionTitle>
                {visibleModules.length ? (
                    <div className='ai-tools-grid'>
                        {visibleModules.map(module => (
                            <ModuleCard
                                key={module.id}
                                module={module}
                                enabled={isEnabled(module)}
                                onToggle={(on) => toggleModule(module.id, on)}
                                onOpen={openFlows[module.live]}
                            />
                        ))}
                    </div>
                ) : (
                    <div className='ai-empty'>{emptyMessage}</div>
                )}
            </section>
        </div>
    )
}

const mapStateToProps = (state) => {
    return {
    };
}

const mapDispatchToProps = (dispatch) => ({
    setFromClassroom: (val) => dispatch(setFromClassroom(val)),
    setQuickAccessItem: (res) => dispatch(setQuickAccessItem(res))
})

export default connect(mapStateToProps, mapDispatchToProps)(AiTools)
